import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
	Search,
	ChevronRight,
	ChevronLeft,
	History,
	Loader2,
} from "lucide-react";
import {
	getMonitoringPromptAnalysis,
	listMonitoringPromptAnalyses,
} from "../../../services/monitoringApi";
import AnalysisStrategyView from "./analysisStrategy/AnalysisStrategyView";
import FanoutQuerySection from "./fanout/FanoutQuerySection";
import { analysisEngineLabel } from "./fanout/fanoutUtils";
import { ProviderIcon, ProviderChip } from "../shared/ProviderIcon";
import { monitoringAnalysisStrategyReturnPath } from "../auditUtils";

// Backoff for getMonitoringPromptAnalysis only (fan-out + content strategy).
const ANALYSIS_POLL_DELAYS_MS = [1000, 2000, 4000, 8000, 10000];

function getAnalysisPollDelayMs(attemptIndex) {
	return attemptIndex < ANALYSIS_POLL_DELAYS_MS.length
		? ANALYSIS_POLL_DELAYS_MS[attemptIndex]
		: ANALYSIS_POLL_DELAYS_MS[ANALYSIS_POLL_DELAYS_MS.length - 1];
}

function shouldPollAnalysis(analysis) {
	const status = analysis?.status;
	return status === "pending" || status === "running";
}

const PAGE_SIZE = 8;

const STATUS_LABEL = {
	pending: "Pending",
	running: "Generating strategy",
	complete: "Complete",
	failed: "Failed",
};

function hasAnalysisResult(analysis) {
	return Boolean(analysis?.result?.run);
}

function isStrategyPending(analysis) {
	if (!hasAnalysisResult(analysis)) return false;
	if (analysis.result.strategy) return false;
	if (analysis.result.strategy_error) return false;
	const status = analysis.status;
	return status === "pending" || status === "running";
}

function AnalysisHistoryPanel({ websiteId, activeAnalysisId, onSelect }) {
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		setPage(1);
	}, [search]);

	const load = useCallback(async () => {
		setLoading(true);
		const res = await listMonitoringPromptAnalyses(websiteId, {
			q: search || undefined,
			page,
			pageSize: PAGE_SIZE,
		});
		setLoading(false);
		if (res.ok) setData(res.page);
	}, [websiteId, search, page]);

	useEffect(() => {
		load();
	}, [load]);

	const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

	return (
		<div className="monitoring__analysis-history">
			<h4 className="monitoring__analysis-history-title">Analysis history</h4>

			<div className="monitoring__analysis-history-search">
				<Search size={14} aria-hidden className="monitoring__analysis-history-search-icon" />
				<input
					type="search"
					placeholder="Search prompts..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					aria-label="Search analysis history"
					className="form-control form-control-sm"
				/>
			</div>

			{loading && !data ? (
				<p className="text-muted small mb-0">Loading history...</p>
			) : !data || data.items.length === 0 ? (
				<p className="text-muted small mb-0">
					{search
						? "No analyses match your search."
						: "No prompts analysed yet click Analysis next to a prompt in the Prompts tab."}
				</p>
			) : (
				<ul className="monitoring__analysis-history-list">
					{data.items.map((item) => (
						<li key={item.analysis_id}>
							<button
								type="button"
								onClick={() => onSelect(item.analysis_id)}
								aria-current={item.analysis_id === activeAnalysisId}
								className={`monitoring__analysis-history-item${
									item.analysis_id === activeAnalysisId
										? " monitoring__analysis-history-item--active"
										: ""
								}`}
							>
								<p className="monitoring__analysis-history-prompt">{item.prompt_text}</p>
								{Boolean(item.persona_id ?? item.personaId) && (
									<p className="monitoring__analysis-history-persona">
										Persona:{" "}
										{item.persona_name ??
											item.personaName ??
											`#${item.persona_id ?? item.personaId}`}
									</p>
								)}
								<p className="monitoring__analysis-history-date">
									{new Date(item.created_at).toLocaleString()}
								</p>
								<div className="monitoring__analysis-history-meta">
									{item.providers.map((p) => (
										<ProviderChip key={p} provider={p} className="monitoring__provider-chip" />
									))}
									{item.status !== "complete" && (
										<span
											className={`monitoring__analysis-status monitoring__analysis-status--${item.status}`}
										>
											{STATUS_LABEL[item.status] || item.status}
										</span>
									)}
								</div>
							</button>
						</li>
					))}
				</ul>
			)}

			{data && data.total > data.page_size && (
				<div className="monitoring__analysis-history-pager">
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary"
						onClick={() => setPage((p) => Math.max(1, p - 1))}
						disabled={page <= 1}
						aria-label="Previous page"
					>
						<ChevronLeft size={14} /> Prev
					</button>
					<span className="small text-muted">
						Page {page} of {totalPages}
					</span>
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary"
						onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
						disabled={page >= totalPages}
						aria-label="Next page"
					>
						Next <ChevronRight size={14} />
					</button>
				</div>
			)}
		</div>
	);
}


function AnalysisLoadingView({ title, description, providers = [], phase = "fetch" }) {
	const steps =
		phase === "fetch"
			? [
					{ label: "Loading analysis", active: true },
					{ label: "Preparing results", active: false },
				]
			: [
					{ label: "Querying AI engines", active: true },
					{ label: "Mapping fan-out", active: false },
					{ label: "Generating strategy", active: false },
				];

	return (
		<div className="monitoring__analysis-loading">
			<div className="monitoring__analysis-loading-glow" aria-hidden />
			<div className="monitoring__analysis-loading-inner">
				<div className="monitoring__analysis-loading-icon">
					<Loader2 size={36} className="monitoring__analysis-spinner" aria-hidden />
				</div>
				<p className="monitoring__analysis-loading-kicker mb-1">
					{phase === "fetch" ? "Loading analysis" : "Analysis in progress"}
				</p>
				<h4 className="monitoring__analysis-loading-title">{title}</h4>
				<p className="monitoring__analysis-loading-desc">{description}</p>
				{providers.length > 0 && (
					<div className="monitoring__analysis-loading-providers">
						{providers.map((p) => (
							<span key={p} className="monitoring__analysis-loading-provider">
								<ProviderIcon provider={p} size={16} />
								{analysisEngineLabel(p)}
							</span>
						))}
					</div>
				)}
				<ol className="monitoring__analysis-loading-steps">
					{steps.map((step) => (
						<li
							key={step.label}
							className={step.active ? "is-active" : undefined}
						>
							<span className="monitoring__analysis-loading-step-dot" aria-hidden />
							{step.label}
						</li>
					))}
				</ol>
			</div>
		</div>
	);
}
function MonitoringAnalysis({ websiteId, onOpenWritingRun, onAnalysisSettled }) {
	const [searchParams, setSearchParams] = useSearchParams();
	const analysisIdParam = searchParams.get("analysisId");
	const viewParam = searchParams.get("view");
	const parsedAnalysisId =
		analysisIdParam != null && analysisIdParam !== ""
			? Number(analysisIdParam)
			: null;
	const viewedAnalysisId = Number.isFinite(parsedAnalysisId)
		? parsedAnalysisId
		: null;

	const [innerView, setInnerView] = useState(() =>
		viewParam === "fanout" ? "fanout" : "strategy"
	);
	const pollAttemptRef = useRef(0);
	const [activeProvider, setActiveProvider] = useState(null);
	const [historyOpen, setHistoryOpen] = useState(true);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	useEffect(() => {
		const nextView = viewParam === "fanout" ? "fanout" : "strategy";
		setInnerView(nextView);
	}, [viewParam]);

	const setAnalysisView = useCallback(
		(view) => {
			setInnerView(view);
			const next = new URLSearchParams(searchParams);
			if (view === "fanout") {
				next.set("view", "fanout");
			} else {
				next.delete("view");
				next.delete("fanoutView");
			}
			setSearchParams(next, { replace: true });
		},
		[searchParams, setSearchParams]
	);

	const openWritingRunFromStrategy = useCallback(
		(runId) => {
			const returnTo = monitoringAnalysisStrategyReturnPath(viewedAnalysisId);
			if (returnTo) {
				onOpenWritingRun?.(runId, { returnTo });
				return;
			}
			onOpenWritingRun?.(runId);
		},
		[onOpenWritingRun, viewedAnalysisId]
	);

	useEffect(() => {
		if (viewedAnalysisId == null || websiteId == null) {
			setData(null);
			setLoading(false);
			setError(null);
			return undefined;
		}

		let cancelled = false;
		setData(null);
		setError(null);
		setLoading(true);

		async function load() {
			const res = await getMonitoringPromptAnalysis(websiteId, viewedAnalysisId);
			if (cancelled) return;
			if (!res.ok) {
				setError(res.message || "Could not load analysis");
				setData(null);
			} else {
				setError(null);
				setData(res.analysis);
			}
			setLoading(false);
		}

		load();
		return () => {
			cancelled = true;
		};
	}, [websiteId, viewedAnalysisId]);

	useEffect(() => {
		pollAttemptRef.current = 0;
	}, [viewedAnalysisId]);

	const pollPhase =
		loading || !data
			? "loading"
			: shouldPollAnalysis(data)
				? "polling"
				: "idle";

	// Poll getMonitoringPromptAnalysis with 1s → 2s → 4s → 8s → 10s, then every 10s.
	useEffect(() => {
		if (websiteId == null || viewedAnalysisId == null) return undefined;
		if (pollPhase !== "polling") return undefined;

		let cancelled = false;
		let timerId;

		function scheduleNextPoll() {
			const delayMs = getAnalysisPollDelayMs(pollAttemptRef.current);
			pollAttemptRef.current += 1;
			timerId = window.setTimeout(runPoll, delayMs);
		}

		async function runPoll() {
			if (cancelled) return;
			const res = await getMonitoringPromptAnalysis(websiteId, viewedAnalysisId);
			if (cancelled) return;
			if (res.ok) setData(res.analysis);
			if (res.ok && !shouldPollAnalysis(res.analysis)) return;
			scheduleNextPoll();
		}

		scheduleNextPoll();

		return () => {
			cancelled = true;
			clearTimeout(timerId);
		};
	}, [websiteId, viewedAnalysisId, pollPhase]);

	useEffect(() => {
		const status = data?.status;
		if (status === "complete" || status === "failed") {
			onAnalysisSettled?.();
		}
	}, [data?.status, onAnalysisSettled]);

	// Fresh analysis from Prompts â†’ collapse history for focus.
	useEffect(() => {
		if (viewedAnalysisId != null) {
			setHistoryOpen(false);
		}
	}, [viewedAnalysisId]);

	useEffect(() => {
		const analyses = data?.result?.run?.analyses;
		if (!analyses?.length) return;
		setActiveProvider((prev) =>
			prev && analyses.some((a) => a.provider === prev)
				? prev
				: analyses[0].provider
		);
	}, [data?.result?.run?.id, data?.result?.run?.analyses]);

	const handleSelectHistory = (id) => {
		const next = new URLSearchParams(searchParams);
		next.set("analysisId", String(id));
		next.delete("promptId");
		next.delete("prompt");
		setSearchParams(next, { replace: true });
	};

	let mainContent;

	if (viewedAnalysisId == null) {
		mainContent = (
			<div className="monitoring__analysis-empty-state">
				<p className="mb-0">
					Click <strong>Analysis</strong> next to a prompt in the Prompts tab (or
					pick a past one from history) to see its fan-out analysis and
					content-strategy recommendations here.
				</p>
			</div>
		);
	} else if (error && !hasAnalysisResult(data)) {
		mainContent = (
			<div className="monitoring__analysis-error">{error}</div>
		);
	} else if (data?.status === "failed" && !hasAnalysisResult(data)) {
		mainContent = (
			<div className="monitoring__analysis-error">
				<p className="fw-semibold mb-1">Analysis failed</p>
				<p className="small mb-0">{data.error_message || "Unknown error."}</p>
			</div>
		);
	} else if (!hasAnalysisResult(data)) {
		const providerList = data?.providers || [];
		const waitingForFanout =
			data?.status === "pending" || data?.status === "running" || loading || !data;
		mainContent = waitingForFanout ? (
			<AnalysisLoadingView
				phase={loading || !data ? "fetch" : "running"}
				title={
					loading || !data
						? "Retrieving your analysis"
						: providerList.length
							? `Analysing across ${providerList.map(analysisEngineLabel).join(", ")}`
							: "Running analysis"
				}
				description={
					loading || !data
						? "Fetching fan-out results and preparing the analysis view."
						: "Mapping citations across providers. Content strategy follows once fan-out is ready."
				}
				providers={providerList}
			/>
		) : (
			<div className="monitoring__analysis-error">
				Analysis marked complete but no result was recorded.
			</div>
		);
	} else {
		const strategyPending = isStrategyPending(data);
		const { run, strategy, strategy_error: strategyError } = data.result;
		const analyses = run.analyses || [];
		const activeAnalysis =
			analyses.find((a) => a.provider === activeProvider) ?? analyses[0] ?? null;
		const brandName = run.brand_name || strategy?.target_brand?.name;
		const brandDomain = run.brand_domain || strategy?.target_brand?.domain;

		mainContent = (
			<div className="monitoring__analysis-main">
				<header className="monitoring__analysis-run-header mb-3">
					<h3 className="monitoring__section-title mb-1">{run.prompt}</h3>
					<p className="text-muted small mb-0">
						{brandName && (
							<span className="fw-medium text-body">{brandName}</span>
						)}
						{brandName && brandDomain && " · "}
						{brandDomain && (
							<>
								Brand domain{" "}
								<code className="monitoring__inline-code">{brandDomain}</code>
							</>
						)}
						{(brandName || brandDomain) && " · "}
						{new Date(run.created_at).toLocaleString()}
					</p>
				</header>

				<div
					className="monitoring__segmented monitoring__segmented--sm mb-3"
					role="group"
					aria-label="Analysis view"
				>
					<button
						type="button"
						className={`monitoring__segmented-btn${
							innerView === "strategy" ? " is-active" : ""
						}`}
						onClick={() => setAnalysisView("strategy")}
						aria-pressed={innerView === "strategy"}
					>
						Content Strategy
					</button>
					<button
						type="button"
						className={`monitoring__segmented-btn${
							innerView === "fanout" ? " is-active" : ""
						}`}
						onClick={() => setAnalysisView("fanout")}
						aria-pressed={innerView === "fanout"}
					>
						Fan-out Query
					</button>
				</div>

				{innerView === "fanout" ? (
					<FanoutQuerySection
						key={`fanout-${run.id}`}
						run={run}
						analyses={analyses}
						activeAnalysis={activeAnalysis}
						activeProvider={activeProvider}
						onSelectProvider={setActiveProvider}
					/>
				) : (
					<AnalysisStrategyView
						key={`strategy-${run.id}`}
						strategy={strategy}
						strategyError={strategyError}
						strategyPending={strategyPending}
						onViewGapAnalysis={() => setAnalysisView("fanout")}
						websiteId={websiteId}
						analysisId={viewedAnalysisId}
						onOpenWritingRun={openWritingRunFromStrategy}
						brandName={brandName}
						brandDomain={brandDomain}
					/>
				)}
			</div>
		);
	}

	return (
		<div className="monitoring__analysis">
			<div className="monitoring__analysis-layout">
				{historyOpen ? (
					<aside className="monitoring__analysis-sidebar">
						<div className="d-flex justify-content-end mb-2">
							<button
								type="button"
								className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
								onClick={() => setHistoryOpen(false)}
								title="Hide history"
							>
								<ChevronRight size={14} /> Hide
							</button>
						</div>
						<AnalysisHistoryPanel
							websiteId={websiteId}
							activeAnalysisId={viewedAnalysisId}
							onSelect={handleSelectHistory}
						/>
					</aside>
				) : (
					<button
						type="button"
						className="monitoring__analysis-history-toggle"
						onClick={() => setHistoryOpen(true)}
						title="Show history"
					>
						<History size={18} aria-hidden />
					</button>
				)}
				<div className="monitoring__analysis-content">{mainContent}</div>
			</div>
		</div>
	);
}

export default MonitoringAnalysis;
